import axios, { AxiosRequestConfig, AxiosResponse, CanceledError } from 'axios';
import { accessToken } from './agent';

export interface WikiEntry {
  wikiEntryID: number;
  url: string;
  tooltipId: string;
  contentHTML: string;
  contentText: string;
  contentImage?: string | null;
  lastUpdatedAt: string;
}

const docsAxios = axios.create();

if (process.env.REACT_APP_ENV_API != undefined) {
  docsAxios.defaults.baseURL = process.env.REACT_APP_ENV_API + '/docsAPI';
} else {
  docsAxios.defaults.baseURL = 'https://creatordev.norskinteraktiv.no/docsAPI';
}
//docsAxios.defaults.baseURL = 'https://localhost:44310/DocsAPI';

docsAxios.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers = config.headers ?? {};
      config.headers.authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

const requests = {
  get: <T>(url: string, config?: AxiosRequestConfig) => docsAxios.get<T>(url, config).then(responseBody),
  post: <T, B = undefined>(url: string, body?: B, config?: AxiosRequestConfig) => docsAxios.post<T>(url, body, config).then(responseBody),
  put: <T, B = undefined>(url: string, body?: B, config?: AxiosRequestConfig) => docsAxios.put<T>(url, body, config).then(responseBody),
  del: (url: string, config?: AxiosRequestConfig) => docsAxios.delete(url, config).then(responseBody),
};

const WikiEntries = {
  list: (urlParam?: string, signal?: AbortSignal) => {
    const query = urlParam ? `?url=${encodeURIComponent(urlParam)}` : '';
    return requests.get<WikiEntry[]>(`/wiki-entries${query}`, { signal });
  },
  getById: (id: number, signal?: AbortSignal) => requests.get<WikiEntry>(`/wiki-entries/${id}`, { signal }),
  getByTooltipId: (tooltipId: string, signal?: AbortSignal) => requests.get<WikiEntry>(`/wiki-entries/by-tooltip-id/${encodeURIComponent(tooltipId)}`, { signal }),
  create: (payload: { url: string; tooltipId: string; lastUpdatedAt?: string }, signal?: AbortSignal) => requests.post<WikiEntry, typeof payload>('/wiki-entries', payload, { signal }),
  update: (id: number, payload: Partial<Omit<WikiEntry, 'wikiEntryID'>>, signal?: AbortSignal) => requests.put<WikiEntry, typeof payload>(`/wiki-entries/${id}`, payload, { signal }),
  delete: (id: number, signal?: AbortSignal) => requests.del(`/wiki-entries/${id}`, { signal }),
  forceUpdate: (payload: { url: string; tooltipIds?: string[] }, signal?: AbortSignal) => requests.post<WikiEntry[], typeof payload>('/wiki-entries/force-update', payload, { signal }),
};

const docsAgent = {
  WikiEntries,
};

export const isDocsRequestCanceled = (error: unknown) => error instanceof CanceledError;

export default docsAgent;
